'use client';

/**
 * ListProgressBar — shows "X of Y done" progress bar on a list detail page (S3.7).
 *
 * Fetches the batch item status for all items in the list and renders a
 * progress bar + counter. Only rendered when the list has ≥ 2 items and the
 * user is signed in (status requires auth).
 *
 * Uses the batch status endpoint to avoid N individual requests.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchUserItemStatuses } from '@/lib/api';
import { Progress } from '@/components/ui/progress';

interface ListProgressBarProps {
  /** MongoDB item IDs for all items in the list. */
  itemIds: string[];
}

export function ListProgressBar({ itemIds }: ListProgressBarProps) {
  const { data } = useQuery({
    queryKey: ['itemStatusBatch', itemIds],
    queryFn: () => fetchUserItemStatuses(itemIds),
    // Re-fetch when individual status pills update — they share the same cache
    // keys so the progress bar stays in sync automatically via the broad key.
    staleTime: 30_000,
    enabled: itemIds.length > 0,
  });

  if (!data || itemIds.length < 2) return null;

  const doneCount = Object.values(data.statuses).filter((s) => s.done).length;
  const total = itemIds.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {doneCount} of {total} done
        </span>
        {doneCount === total && total > 0 && (
          <span className="text-xs font-medium text-primary">All done!</span>
        )}
      </div>
      <Progress value={pct} />
    </div>
  );
}
